import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Icon } from './icons';

/**
 * The colours a list or label can be: the server's names, in Todoist's
 * order. Each is a --color-swatch-* token (App.css), reached through a
 * `sw-<name>` class so no colour is ever written in a component.
 */
export const COLORS = ['red', 'orange', 'yellow', 'green', 'teal', 'blue', 'purple', 'pink', 'grey'];
export const swatch = (c) => (COLORS.includes(c) ? `sw-${c}` : 'sw-none');
export const colorName = (c) => (COLORS.includes(c) ? c[0].toUpperCase() + c.slice(1) : 'No colour');

/** A row of colour buttons; `value` null is "no colour". */
export function ColorPicker({ value, onPick, name }) {
  return (
    <div className="tda-swatches" role="group" aria-label={`Colour of ${name}`}>
      {[null, ...COLORS].map((c) => (
        <button key={c || 'none'} type="button" className={`tda-swatch ${swatch(c)}`}
                aria-label={`Colour: ${colorName(c)}`} aria-pressed={(value || null) === c}
                title={colorName(c)} onClick={() => onPick(c)} />
      ))}
    </div>
  );
}

/**
 * A menu item that opens a little more of the menu under itself - "Colour ▸"
 * and its swatches (Oct 2026: the colour grid no longer fills every ⋯).
 */
function SubItem({ it, close }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" role="menuitem" className="tda-menu-item tda-menu-sub" aria-expanded={open}
              onClick={() => setOpen((v) => !v)}>
        {it.icon && <Icon name={it.icon} />}
        <span className="tda-menu-label">{it.label}</span>
        <Icon name={open ? 'chevronDown' : 'chevronRight'} size={13} className="tda-menu-caret" />
      </button>
      {open && <div className="tda-menu-node">{it.sub(close)}</div>}
    </>
  );
}

/**
 * The ⋯ on a row, a list, a label or a section: a small menu of what can be
 * done to it. Closes on a choice, on Escape and on a press anywhere else; the
 * arrow keys walk it. `items` are { label, onClick, danger, icon, hint, title }
 * (icon: a name from icons.js; hint: its key, "E"), { sep: true } for a rule
 * between groups, or { node } for something that is not a button (the colour
 * row). Falsy items are skipped, and a rule never starts or ends the menu or
 * follows another.
 *
 * It opens downwards, or upwards when there is no room below, and never
 * wider than the screen.
 */
export default function MoreMenu({ label, items, disabled = false, icon = 'more' }) {
  const [open, setOpen] = useState(false);
  const [up, setUp] = useState(false);
  const [room, setRoom] = useState(null);   // the most it may be tall, so it never runs under the bar or off the screen
  const ref = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
        const b = ref.current && ref.current.querySelector('.tda-more-btn');
        if (b) b.focus();
        return;
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const all = menuRef.current ? [...menuRef.current.querySelectorAll('[role="menuitem"]:not(:disabled)')] : [];
      if (!all.length) return;
      e.preventDefault();
      e.stopPropagation();
      const at = all.indexOf(document.activeElement);
      const next = e.key === 'ArrowDown' ? (at + 1) % all.length : (at <= 0 ? all.length - 1 : at - 1);
      all[next].focus();
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  // Room below? Measured once it is drawn; otherwise it opens upwards.
  useLayoutEffect(() => {
    if (!open || !menuRef.current || !ref.current) { setUp(false); setRoom(null); return; }
    const btn = ref.current.getBoundingClientRect();
    const h = menuRef.current.scrollHeight;
    // Oct 2026: the app's fixed bar counts as the top of the screen, and a
    // menu longer than the room either way takes the larger side and scrolls.
    const bar = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--navbar-h')) || 0;
    const below = window.innerHeight - btn.bottom - 12;
    const above = btn.top - bar - 12;
    const goUp = h > below && above > below;
    setUp(goUp);
    setRoom(h > (goUp ? above : below) ? Math.max(160, goUp ? above : below) : null);
  }, [open]);

  const shown = [];
  items.filter(Boolean).forEach((it) => {
    if (it.sep) { if (shown.length && !shown[shown.length - 1].sep) shown.push(it); return; }
    shown.push(it);
  });
  while (shown.length && shown[shown.length - 1].sep) shown.pop();

  return (
    <div className="tda-more" ref={ref}>
      <button type="button" className="tda-more-btn" aria-label={label} title={label}
              aria-haspopup="true" aria-expanded={open} disabled={disabled}
              onClick={() => setOpen((v) => !v)}><Icon name={icon} /></button>
      {open && (
        <div className={`tda-menu${up ? ' is-up' : ''}`} role="menu" aria-label={label} ref={menuRef}
             style={room ? { maxHeight: room, overflowY: 'auto' } : undefined}>
          {shown.map((it, i) => (it.sep ? (
            <div key={`s${i}`} className="tda-menu-sep" role="separator" />
          ) : it.node ? (
            <div key={`n${i}`} className="tda-menu-node">{it.node(() => setOpen(false))}</div>
          ) : it.sub ? (
            <SubItem key={`u${i}`} it={it} close={() => setOpen(false)} />
          ) : (
            <button key={it.label} type="button" role="menuitem"
                    className={`tda-menu-item${it.danger ? ' is-danger' : ''}`}
                    disabled={it.disabled} title={it.title || undefined}
                    onClick={() => { setOpen(false); it.onClick(); }}>
              {it.icon && <Icon name={it.icon} />}
              <span className="tda-menu-label">{it.label}</span>
              {it.hint && <kbd className="tda-menu-hint" aria-hidden="true">{it.hint}</kbd>}
            </button>
          )))}
        </div>
      )}
    </div>
  );
}
